import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Upload, Filter, ChevronDown, ChevronLeft, ChevronRight,
  SlidersHorizontal, X, Building2, MapPin, Briefcase, Users, Check,
  Mail, Eye, FolderOpen, Plus, Loader2, Tag, Trash2, Workflow, Clock, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, del } from '@/lib/apiClient';
import { API_URL, STORAGE } from '@/_config';
import ConfirmModal from '@/components/ConfirmModal';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { useAuth } from '@/context/AuthContext';

interface Lead {
  id: string;
  name: string;
  company: string;
  title: string;
  industry: string;
  location: string;
  source: string;
  email_status?: string | null;
  added: string;
  email?: string | null;
  phone?: string | null;
  is_unsubscribed?: boolean;
}

interface LeadFilterOptions {
  industries: string[];
  companies: string[];
  titles: string[];
  locations: string[];
}

// ─── Static filter options ────────────────────────────────────────────────────



const SOURCE_OPTIONS = ['Import - data', 'Others']; // 'Apollo' hidden temporarily — do not remove Apollo from logic

const SENIORITY_OPTIONS = [
  { value: 'c_suite', label: 'C-Suite' },
  { value: 'vp', label: 'VP' },
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior', label: 'Senior' },
  { value: 'individual', label: 'Individual Contributor' },
  { value: 'entry', label: 'Entry Level' },
  { value: 'intern', label: 'Intern' },
];

const EMAIL_STATUS_OPTIONS = [
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'unavailable', label: 'Unavailable' },
];

// ─── Reusable dropdown component ─────────────────────────────────────────────

function FilterDropdown({
  filterKey, options, selected, onToggle, onClear, placeholder, searchValue, onSearchChange, allowCustom = false,
}: {
  filterKey: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  onClear: () => void;
  placeholder: string;
  searchValue: string;
  onSearchChange: (v: string) => void;
  allowCustom?: boolean;
}) {
  const trimmed = searchValue.trim();
  const filtered = options.filter(o => o.toLowerCase().includes(searchValue.toLowerCase()));
  const hasExactMatch = options.some(o => o.toLowerCase() === trimmed.toLowerCase());
  const showCustomHint = allowCustom && trimmed.length > 0 && !hasExactMatch && !selected.includes(trimmed);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && allowCustom && trimmed && !selected.includes(trimmed)) {
      onToggle(trimmed);
      onSearchChange('');
    }
  };

  return (
    <div className="ld-dropdown" onClick={e => e.stopPropagation()}>
      <div className="ld-dropdown-search-wrap">
        <Search size={13} className="ld-dd-search-icon" />
        <input
          className="ld-dd-search-input"
          placeholder={placeholder}
          value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        {searchValue && (
          <button className="ld-dd-search-clear" onClick={() => onSearchChange('')}><X size={11} /></button>
        )}
      </div>
      <div className="ld-dropdown-list">
        {showCustomHint && (
          <button
            className="ld-dd-item ld-dd-item--custom"
            onClick={() => { onToggle(trimmed); onSearchChange(''); }}
          >
            <span className="ld-dd-checkbox" />
            <span className="ld-dd-item-label">
              Add &ldquo;<strong>{trimmed}</strong>&rdquo;
            </span>
          </button>
        )}
        {filtered.map(option => (
          <button
            key={option}
            className={`ld-dd-item ${selected.includes(option) ? 'ld-dd-item--checked' : ''}`}
            onClick={() => onToggle(option)}
          >
            <span className="ld-dd-checkbox">{selected.includes(option) && <Check size={10} />}</span>
            <span className="ld-dd-item-label">{option}</span>
          </button>
        ))}
        {filtered.length === 0 && !showCustomHint && searchValue.trim() !== '' && (
          <div className="ld-dd-empty">No results for &ldquo;{searchValue}&rdquo;</div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="ld-dropdown-footer">
          <button className="ld-dd-clear-btn" onClick={onClear}>Clear ({selected.length})</button>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const tabs = ['All Leads', 'My Segments', 'Recently Viewed'];

const sourceBadge = (source: string): string => {
  const map: Record<string, string> = { LinkedIn: 'badge-blue', 'Import - data': 'badge-green', website: 'badge-green', Referral: 'badge-amber', Conference: 'badge-gray', Import: 'badge-gray', Apollo: 'badge-blue', 'Internal Database': 'badge-blue' };
  return map[source] || 'badge-gray';
}
const sourceLabel = (source: string): string => {
  if (source === 'website') return 'Import - data';
  return source;
};

interface Segment { id: string; name: string; lead_count: number; created_at: string; }

export default function LeadsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isPremiumPlan = !!(user?.organization?.plan_features?.campaign_features);
  const isAdminPlan = user?.organization?.plan === 'admin';
  const showByDefault = !isPremiumPlan &&
    (user?.planStatus === 'active' || user?.planStatus === 'trialing');
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedLeadMap, setSelectedLeadMap] = useState<Record<string, Lead>>({});
  const [allPagesSelected, setAllPagesSelected] = useState(false);
  const returnTab = (location.state as any)?.returnTab;
  const [activeTab, setActiveTab] = useState<string>(returnTab ?? 'All Leads');

  // Restore search context when navigating back from Recently Viewed
  const restoredCtx = (location.state as any)?.restoreContext;
  const [searchQuery, setSearchQuery] = useState<string>(restoredCtx?.query ?? '');
  const [committedSearch, setCommittedSearch] = useState<string>(restoredCtx?.query ?? '');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(500);
  const [goToPageInput, setGoToPageInput] = useState<string>('');
  const [goToPageError, setGoToPageError] = useState<string>('');

  // Filter values — initialised from restored context if coming from Recently Viewed
  const [filterTitles, setFilterTitles] = useState<string[]>(restoredCtx?.titles ?? []);
  const [filterLocations, setFilterLocations] = useState<string[]>(restoredCtx?.locations ?? []);
  const [filterCompany, setFilterCompany] = useState<string[]>(restoredCtx?.company ?? []);
  const [filterIndustry, setFilterIndustry] = useState<string[]>(restoredCtx?.industry ?? []);
  const [filterSource, setFilterSource] = useState<string[]>(restoredCtx?.source ?? []);
  const [filterSeniority, setFilterSeniority] = useState<string[]>(restoredCtx?.seniority ?? []);
  const [filterEmailStatus, setFilterEmailStatus] = useState<string[]>(restoredCtx?.emailStatus ?? []);

  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [ddSearch, setDdSearch] = useState<Record<string, string>>({});
  const filterRowRef = useRef<HTMLDivElement>(null);

  // Segment modal state
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [segmentName, setSegmentName] = useState('');
  const [segmentError, setSegmentError] = useState('');
  const [confirmDeleteSeg, setConfirmDeleteSeg] = useState<{ id: string; name: string } | null>(null);
  const [confirmDeleteLeads, setConfirmDeleteLeads] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);

  // Recently Viewed
  const { items: recentLeads, clear: clearRecentLeads } = useRecentlyViewed();

  // Resubscribe state (admin plan only)
  const [resubscribeLeadId, setResubscribeLeadId] = useState<string | null>(null);
  const removeUnsubscribeMutation = useMutation({
    mutationFn: (leadId: string) => del(API_URL.LEADS.REMOVE_UNSUBSCRIBE(leadId)),
    onSuccess: (_, leadId) => {
      queryClient.setQueryData(['leads', queryParams], (old: any) => {
        if (!old?.data?.leads) return old;
        return {
          ...old,
          data: {
            ...old.data,
            leads: old.data.leads.map((l: Lead) =>
              l.id === leadId ? { ...l, is_unsubscribed: false } : l
            ),
          },
        };
      });
      toast.success('Lead removed from unsubscribe list.');
    },
    onError: () => toast.error('Failed to remove from unsubscribe list.'),
  });

  // Export CSV state
  const [isExporting, setIsExporting] = useState(false);

  // Import CSV modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);

  // Fetch existing segments (for "My Segments" tab + modal list)
  const { data: segmentsData, isLoading: segmentsLoading } = useQuery({
    queryKey: ['segments'],
    queryFn: () => get<{ segments: Segment[] }>(API_URL.SEGMENTS.LIST),
  });
  const segments: Segment[] = segmentsData?.data?.segments ?? [];

  // Create new segment mutation
  const createSegmentMutation = useMutation({
    mutationFn: (payload: { name: string; leads?: Lead[]; filters?: Record<string, unknown> }) =>
      post<{ segment: Segment }>(API_URL.SEGMENTS.CREATE, {
        name: payload.name,
        ...(payload.filters
          ? { filters: payload.filters }
          : {
              leads: (payload.leads ?? []).map(l => ({
                id: l.id, name: l.name, company: l.company,
                title: l.title, industry: l.industry, location: l.location, email: l.email, source: l.source,
              })),
            }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments'] });
      setShowSegmentModal(false);
      setSegmentName('');
      setSelected([]);
      setSelectedLeadMap({});
      setAllPagesSelected(false);
      toast.success('Segment created successfully.');
    },
    onError: () => {
      setSegmentError('Failed to create segment. Please try again.');
      toast.error('Failed to create segment. Please try again.');
    },
  });

  // Add leads to existing segment mutation
  const addToSegmentMutation = useMutation({
    mutationFn: (payload: { segmentId: string; leads?: Lead[]; filters?: Record<string, unknown> }) =>
      post(API_URL.SEGMENTS.ADD_LEADS(payload.segmentId), {
        ...(payload.filters
          ? { filters: payload.filters }
          : {
              leads: (payload.leads ?? []).map(l => ({
                id: l.id, name: l.name, company: l.company,
                title: l.title, industry: l.industry, location: l.location, email: l.email, source: l.source,
              })),
            }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments'] });
      setShowSegmentModal(false);
      setSegmentName('');
      setSelected([]);
      setSelectedLeadMap({});
      setAllPagesSelected(false);
      toast.success('Leads added to segment successfully.');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setSegmentError(msg ?? 'Failed to add leads. Please try again.');
      toast.error(msg ?? 'Failed to add leads. Please try again.');
    },
  });

  // Delete segment mutation
  const deleteSegmentMutation = useMutation({
    mutationFn: (id: string) => del(API_URL.SEGMENTS.DELETE(id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });

  // Delete leads mutation
  const deleteLeadsMutation = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map(id => del(API_URL.LEADS.DELETE(id)))),
    onSuccess: (_, ids) => {
      const count = ids.length;
      toast.success(count === 1 ? 'Lead deleted.' : `${count} leads deleted.`);
      setSelected([]); setSelectedLeadMap({});
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: () => toast.error('Failed to delete lead(s). Please try again.'),
  });

  const isSaving = createSegmentMutation.isPending || addToSegmentMutation.isPending;
  const deletableLeads = Object.values(selectedLeadMap).filter(l => l.source?.toLowerCase() === 'website');

  const openSegmentModal = () => {
    setSegmentName('');
    setSegmentError('');
    setShowSegmentModal(true);
  };

  const handleCreateSegment = () => {
    const name = segmentName.trim();
    if (!name) { setSegmentError('Please enter a segment name.'); return; }
    if (allPagesSelected) {
      const { page: _p, per_page: _pp, ...filterParams } = queryParams;
      createSegmentMutation.mutate({ name, filters: filterParams });
    } else {
      createSegmentMutation.mutate({ name, leads: Object.values(selectedLeadMap) });
    }
  };

  const handleAddToExisting = (segmentId: string) => {
    setSegmentError('');
    if (allPagesSelected) {
      const { page: _p, per_page: _pp, ...filterParams } = queryParams;
      addToSegmentMutation.mutate({ segmentId, filters: filterParams });
    } else {
      addToSegmentMutation.mutate({ segmentId, leads: Object.values(selectedLeadMap) });
    }
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await post<{ imported: number; skipped: number; errors: string[]; message: string }>(
        API_URL.LEADS.IMPORT,
        formData,
      );
      setImportResult({
        imported: response.data?.imported ?? 0,
        skipped: response.data?.skipped ?? 0,
        errors: response.data?.errors ?? [],
      });
      setImportFile(null);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    } catch (err: unknown) {
      const message = (err instanceof Error ? err.message : null) || 'Import failed';
      setImportResult({ imported: 0, skipped: 0, errors: [message] });
    } finally {
      setImporting(false);
    }
  };

  // Reset page when filters or per-page size changes
  useEffect(() => { setPage(1); }, [filterTitles, filterLocations, filterCompany, filterIndustry, filterSource, filterSeniority, filterEmailStatus, perPage]);

  // Clear go-to-page input and error whenever the page changes
  useEffect(() => { setGoToPageInput(''); setGoToPageError(''); }, [page]);

  // When context filters change, clear dependent filter selections — they may no longer be valid
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFilterCompany([]); setFilterLocations([]); setFilterTitles([]); }, [filterSource.join(','), filterIndustry.join(',')]);

  // Auto-select Apollo source — temporarily disabled while Apollo is hidden from the source dropdown.
  // Re-enable by uncommenting when Apollo is restored to SOURCE_OPTIONS.
  // useEffect(() => {
  //   if (filterSource.length === 0) {
  //     const anyActive = committedSearch.length > 0 ||
  //       filterTitles.length > 0 || filterLocations.length > 0 ||
  //       filterCompany.length > 0 || filterIndustry.length > 0 || filterSeniority.length > 0 || filterEmailStatus.length > 0;
  //     if (anyActive) setFilterSource(['Apollo']);
  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [committedSearch, filterTitles.length, filterLocations.length, filterCompany.length, filterIndustry.length, filterSeniority.length, filterEmailStatus.length]);

  // Click-outside closes all dropdowns
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRowRef.current && !filterRowRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleDropdown = (name: string) => {
    setOpenDropdown(prev => prev === name ? null : name);
    setDdSearch(prev => ({ ...prev, [name]: '' }));
  };

  const toggleValue = (setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    setSelected([]); setSelectedLeadMap({}); setAllPagesSelected(false);
  };

  const clearAllFilters = () => {
    setFilterTitles([]); setFilterLocations([]); setFilterCompany([]);
    setFilterIndustry([]); setFilterSource([]); setFilterSeniority([]); setFilterEmailStatus([]);
    setOpenDropdown(null);
    setSelected([]); setSelectedLeadMap({}); setAllPagesSelected(false);
  };

  const hasActiveFilters = filterTitles.length > 0 || filterLocations.length > 0 ||
    filterCompany.length > 0 || filterIndustry.length > 0 || filterSource.length > 0 ||
    filterSeniority.length > 0 || filterEmailStatus.length > 0;

  const handleGoToPage = () => {
    const num = parseInt(goToPageInput, 10);
    if (!goToPageInput.trim() || isNaN(num) || !Number.isInteger(num)) {
      setGoToPageError('Enter a valid page number.');
      return;
    }
    if (num < 1 || num > lastPage) {
      setGoToPageError(`Must be between 1 and ${lastPage}.`);
      return;
    }
    setGoToPageError('');
    setPage(num);
  };

  // Map UI source label to API source param value.
  // Non-premium plans are always restricted to the imported data source.
  const sourceParam = !isPremiumPlan
    ? 'website'
    : filterSource.length === 1
      ? (filterSource[0] === 'Import - data' ? 'website' : filterSource[0] === 'Apollo' ? 'apollo' : filterSource[0] === 'Others' ? 'others' : filterSource[0])
      : undefined;

  const queryParams: Record<string, unknown> = {
    page, per_page: perPage,
    ...(committedSearch && { search: committedSearch }),
    ...(filterTitles.length && { title: filterTitles.join(',') }),
    // Use | separator — location values contain commas (e.g. "Belo Horizonte, Brazil")
    ...(filterLocations.length && { location: filterLocations.join('|') }),
    ...(filterCompany.length && { company: filterCompany[0] }),
    ...(filterIndustry.length && { industry: filterIndustry[0] }),
    ...(filterSeniority.length && { seniority: filterSeniority.join(',') }),
    ...(filterEmailStatus.length && { email_status: filterEmailStatus.join(',') }),
    ...(sourceParam !== undefined && { source: sourceParam }),
  };

  const hasQuery = !!(committedSearch || hasActiveFilters);
  const shouldShowLeads = hasQuery || showByDefault;

  const handleSearch = () => {
    setCommittedSearch(searchQuery);
    setPage(1);
    setSelected([]);
    setSelectedLeadMap({});
    setAllPagesSelected(false);
  };

  const handleReset = () => {
    setSearchQuery('');
    setCommittedSearch('');
    setPage(1);
    setSelected([]);
    setSelectedLeadMap({});
    setAllPagesSelected(false);
  };

  async function handleExport() {
    if (isExporting || totalLeads === 0) return;
    setIsExporting(true);
    try {
      const raw = localStorage.getItem(STORAGE);
      const token: string | undefined = raw
        ? (JSON.parse(raw) as any)?.access_token ?? (JSON.parse(raw) as any)?.token
        : undefined;
      const body: Record<string, unknown> = {};
      if (selected.length > 0 && !allPagesSelected) {
        // Export only the explicitly selected leads — send as array to avoid URL length limits
        body.ids = selected;
      } else {
        // Export all leads matching the current filters
        if (committedSearch)           body.search       = committedSearch;
        if (filterTitles.length)       body.title        = filterTitles.join(',');
        if (filterLocations.length)    body.location     = filterLocations.join('|');
        if (filterCompany.length)      body.company      = filterCompany[0];
        if (filterIndustry.length)     body.industry     = filterIndustry[0];
        if (filterSeniority.length)    body.seniority    = filterSeniority.join(',');
        if (filterEmailStatus.length)  body.email_status = filterEmailStatus.join(',');
        if (sourceParam !== undefined) body.source       = String(sourceParam);
      }
      const response = await fetch(API_URL.LEADS.EXPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] ?? `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch { } finally {
      setIsExporting(false);
    }
  }

  const { data: leadsData, isLoading, isError } = useQuery({
    queryKey: ['leads', queryParams],
    queryFn: () => get<{ leads: Lead[] }>(API_URL.LEADS.LIST, queryParams),
    placeholderData: (prev) => prev,
    enabled: shouldShowLeads,
    staleTime: 5 * 60 * 1000,
  });

  // Clear stale data immediately when there is no active query (e.g. after clearing filters/search)
  const leads: Lead[] = shouldShowLeads ? (leadsData?.data?.leads ?? []) : [];
  const totalLeads: number = shouldShowLeads ? ((leadsData?.meta as any)?.total ?? 0) : 0;

  // Derive unique industry options from current result set (industry remains a context filter)
  const uniqueIndustries = useMemo(() => {
    const normalized = leads.map(l => l.industry).filter(v => v?.trim())
      .map(i => i.charAt(0).toUpperCase() + i.slice(1).toLowerCase());
    return [...new Set(normalized)].sort() as string[];
  }, [leads]);

  // Context filters drive dependent filter options via API
  const hasContextFilters = filterSource.length > 0 || filterIndustry.length > 0;

  const filterOptionsParams = {
    ...(sourceParam !== undefined && { source: sourceParam }),
    ...(filterIndustry.length > 0 && { industry: filterIndustry[0] }),
  };

  const {
    data: filterOptionsData,
    isFetching: filterOptionsFetching,
  } = useQuery({
    queryKey: ['leads-filter-options', filterOptionsParams],
    queryFn: () => get<LeadFilterOptions>(API_URL.LEADS.FILTER_OPTIONS, filterOptionsParams),
    enabled: shouldShowLeads,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const serverIndustries = filterOptionsData?.data?.industries ?? [];
  const serverCompanies  = filterOptionsData?.data?.companies  ?? [];
  const serverTitles     = filterOptionsData?.data?.titles     ?? [];
  const serverLocations  = filterOptionsData?.data?.locations  ?? [];

  const isValidOption = (v: string | null | undefined) => !!v?.trim() && v.trim().length >= 2 && /[a-zA-Z]/.test(v);

  const industryOptions = useMemo(
    () => [...new Set([...serverIndustries, ...filterIndustry])].filter(isValidOption).sort(),
    [serverIndustries, filterIndustry],
  );

  const companyOptions = useMemo(
    () => hasContextFilters
      ? [...new Set([...serverCompanies, ...filterCompany])].filter(isValidOption).sort()
      : [...new Set(leads.map(l => l.company).filter(isValidOption))].sort() as string[],
    [hasContextFilters, serverCompanies, filterCompany, leads],
  );
  const titleOptions = useMemo(
    () => hasContextFilters
      ? [...new Set([...serverTitles, ...filterTitles])].filter(isValidOption).sort()
      : [...new Set(leads.map(l => l.title).filter(isValidOption))].sort() as string[],
    [hasContextFilters, serverTitles, filterTitles, leads],
  );
  const locationOptions = useMemo(() => {
    const serverOpts = [...new Set([...serverLocations, ...filterLocations])].filter(v => v?.trim()).sort();
    if (serverOpts.length > 0) return serverOpts;
    return [...new Set(leads.map(l => l.location).filter(v => v?.trim()))].sort() as string[];
  }, [serverLocations, filterLocations, leads]);

  const lastPage: number = hasQuery ? ((leadsData?.meta as any)?.last_page ?? 1) : 1;
  const showingFrom = leads.length > 0 ? (page - 1) * perPage + 1 : 0;
  const showingTo = Math.min(page * perPage, totalLeads);

  const toggleSelect = (lead: Lead) => {
    setAllPagesSelected(false);
    setSelected(prev =>
      prev.includes(lead.id) ? prev.filter(x => x !== lead.id) : [...prev, lead.id],
    );
    setSelectedLeadMap(prev => {
      if (prev[lead.id]) {
        const next = { ...prev };
        delete next[lead.id];
        return next;
      }
      return { ...prev, [lead.id]: lead };
    });
  };
  const selectableLeads = leads.filter(l => !l.is_unsubscribed);
  const allCurrentPageSelected = selectableLeads.length > 0 && selectableLeads.every(l => selected.includes(l.id));
  const toggleAll = () => {
    if (allCurrentPageSelected) {
      const currentIds = new Set(leads.map(l => l.id));
      setSelected(prev => prev.filter(id => !currentIds.has(id)));
      setSelectedLeadMap(prev => {
        const next = { ...prev };
        leads.forEach(l => delete next[l.id]);
        return next;
      });
      setAllPagesSelected(false);
    } else {
      setSelected(prev => [...new Set([...prev, ...selectableLeads.map(l => l.id)])]);
      setSelectedLeadMap(prev => {
        const next = { ...prev };
        selectableLeads.forEach(l => { next[l.id] = l; });
        return next;
      });
      setAllPagesSelected(false);
    }
  };

  const selectAllPages = () => setAllPagesSelected(true);
  const clearAllPagesSelection = () => {
    setAllPagesSelected(false);
    setSelected([]);
    setSelectedLeadMap({});
  };

  const displaySelectedCount = allPagesSelected ? totalLeads : selected.length;

  return (
    <div className="ld-page">
      {/* Header */}
      <div className="ld-header">
        <h1 className="ld-title">Leads</h1>
        <div className="ld-header-actions">
          {activeTab === 'All Leads' && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleExport}
              disabled={totalLeads === 0 || isExporting}
              title={totalLeads === 0 ? 'No leads to export' : 'Export filtered leads as CSV'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Download size={16} />
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </button>
          )}
          {activeTab === 'All Leads' && <button className="btn btn-secondary btn-sm" onClick={() => setShowImportModal(true)}><Upload size={16} /> Import CSV</button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="ld-tabs">
        {tabs.map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); setSelected([]); setSelectedLeadMap({}); }} className={`ld-tab ${activeTab === tab ? 'ld-tab--active' : ''}`}>
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'All Leads' && (<>
      {/* Search bar */}
      <div className="ld-search-bar">
        <Search size={18} className="ld-search-bar-icon" />
        <input
          className="ld-search-bar-input"
          placeholder="Search by name, company, or keyword..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSearchQuery(val);
            if (val === '') handleReset();
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') handleSearch();
          }}
        />
        {(searchQuery || committedSearch) && (
          <button className="ld-search-clear" onClick={handleReset} title="Clear search"><X size={14} /></button>
        )}
        <span className="ld-search-divider" />
        <button className="ld-search-submit" onClick={handleSearch}>
          <Search size={14} />
          Search
        </button>
      </div>

      {/* Filter chips row */}
      <div className="ld-filter-row" ref={filterRowRef}>
        <div className="ld-filter-chips">

          {/* Job Title */}
          <div className="ld-chip-wrap">
            <button
              className={`ld-chip ${filterTitles.length ? 'ld-chip--active' : ''}`}
              onClick={() => toggleDropdown('title')}
            >
              <Briefcase size={14} />
              Job Title
              {filterTitles.length > 0 && <span className="ld-chip-count">{filterTitles.length}</span>}
              {filterOptionsFetching && hasContextFilters
                ? <Loader2 size={12} className="animate-spin" />
                : <ChevronDown size={12} className={openDropdown === 'title' ? 'ld-chip-chevron--open' : ''} />}
            </button>
            {openDropdown === 'title' && (
              <FilterDropdown
                filterKey="title"
                options={titleOptions}
                selected={filterTitles}
                onToggle={v => toggleValue(setFilterTitles, v)}
                onClear={() => setFilterTitles([])}
                placeholder="Search or type a title..."
                searchValue={ddSearch.title || ''}
                onSearchChange={v => setDdSearch(prev => ({ ...prev, title: v }))}
                allowCustom
              />
            )}
          </div>

          {/* Company */}
          <div className="ld-chip-wrap">
            <button
              className={`ld-chip ${filterCompany.length ? 'ld-chip--active' : ''}`}
              onClick={() => toggleDropdown('company')}
            >
              <Building2 size={14} />
              Company
              {filterCompany.length > 0 && <span className="ld-chip-count">{filterCompany.length}</span>}
              {filterOptionsFetching && hasContextFilters
                ? <Loader2 size={12} className="animate-spin" />
                : <ChevronDown size={12} className={openDropdown === 'company' ? 'ld-chip-chevron--open' : ''} />}
            </button>
            {openDropdown === 'company' && (
              <FilterDropdown
                filterKey="company"
                options={companyOptions}
                selected={filterCompany}
                onToggle={v => setFilterCompany(prev => prev.includes(v) ? [] : [v])}
                onClear={() => setFilterCompany([])}
                placeholder="Search or type a company..."
                searchValue={ddSearch.company || ''}
                onSearchChange={v => setDdSearch(prev => ({ ...prev, company: v }))}
                allowCustom
              />
            )}
          </div>

          {/* Location */}
          <div className="ld-chip-wrap">
            <button
              className={`ld-chip ${filterLocations.length ? 'ld-chip--active' : ''}`}
              onClick={() => toggleDropdown('location')}
            >
              <MapPin size={14} />
              Location
              {filterLocations.length > 0 && <span className="ld-chip-count">{filterLocations.length}</span>}
              {filterOptionsFetching && hasContextFilters
                ? <Loader2 size={12} className="animate-spin" />
                : <ChevronDown size={12} className={openDropdown === 'location' ? 'ld-chip-chevron--open' : ''} />}
            </button>
            {openDropdown === 'location' && (
              <FilterDropdown
                filterKey="location"
                options={locationOptions}
                selected={filterLocations}
                onToggle={v => toggleValue(setFilterLocations, v)}
                onClear={() => setFilterLocations([])}
                placeholder="Search or type a location..."
                searchValue={ddSearch.location || ''}
                onSearchChange={v => setDdSearch(prev => ({ ...prev, location: v }))}
                allowCustom
              />
            )}
          </div>

          {/* Industry — type to search, filters by industry column */}
          <div className="ld-chip-wrap">
            <button
              className={`ld-chip ${filterIndustry.length ? 'ld-chip--active' : ''}`}
              onClick={() => toggleDropdown('industry')}
            >
              <Users size={14} />
              Industry
              {filterIndustry.length > 0 && <span className="ld-chip-count">{filterIndustry.length}</span>}
              <ChevronDown size={12} className={openDropdown === 'industry' ? 'ld-chip-chevron--open' : ''} />
            </button>
            {openDropdown === 'industry' && (
              <FilterDropdown
                filterKey="industry"
                options={industryOptions}
                selected={filterIndustry}
                onToggle={v => setFilterIndustry(prev => prev.includes(v) ? [] : [v])}
                onClear={() => setFilterIndustry([])}
                placeholder="Type an industry to filter..."
                searchValue={ddSearch.industry || ''}
                onSearchChange={v => setDdSearch(prev => ({ ...prev, industry: v }))}
                allowCustom
              />
            )}
          </div>

          {/* Source — only visible for Gold / Platinum plans */}
          {isPremiumPlan && (
            <div className="ld-chip-wrap">
              <button
                className={`ld-chip ${filterSource.length ? 'ld-chip--active' : ''}`}
                onClick={() => toggleDropdown('source')}
              >
                <Filter size={14} />
                Source
                {filterSource.length > 0 && <span className="ld-chip-count">{filterSource.length}</span>}
                <ChevronDown size={12} className={openDropdown === 'source' ? 'ld-chip-chevron--open' : ''} />
              </button>
              {openDropdown === 'source' && (
                <FilterDropdown
                  filterKey="source"
                  options={SOURCE_OPTIONS}
                  selected={filterSource}
                  onToggle={v => setFilterSource(prev => prev.includes(v) ? [] : [v])}
                  onClear={() => setFilterSource([])}
                  placeholder="Search sources..."
                  searchValue={ddSearch.source || ''}
                  onSearchChange={v => setDdSearch(prev => ({ ...prev, source: v }))}
                />
              )}
            </div>
          )}


          {/* Clear filters — shown to the right of More Filters */}
          {hasActiveFilters && (
            <button className="ld-clear-all-btn" onClick={clearAllFilters}>
              <X size={12} /> Clear filters
            </button>
          )}

        </div>
        {hasQuery && <span className="ld-result-count">{totalLeads.toLocaleString()} results</span>}
      </div>

      {/* Active filter tags */}
      {hasActiveFilters && (
        <div className="ld-active-filters">
          {filterTitles.map(t => (
            <span key={t} className="ld-active-tag">
              <Briefcase size={11} /> {t}
              <button onClick={() => toggleValue(setFilterTitles, t)}><X size={10} /></button>
            </span>
          ))}
          {filterLocations.map(l => (
            <span key={l} className="ld-active-tag">
              <MapPin size={11} /> {l}
              <button onClick={() => toggleValue(setFilterLocations, l)}><X size={10} /></button>
            </span>
          ))}
          {filterCompany.map(c => (
            <span key={c} className="ld-active-tag">
              <Building2 size={11} /> {c}
              <button onClick={() => toggleValue(setFilterCompany, c)}><X size={10} /></button>
            </span>
          ))}
          {filterIndustry.map(ind => (
            <span key={ind} className="ld-active-tag">
              <Users size={11} /> {ind}
              <button onClick={() => setFilterIndustry([])}><X size={10} /></button>
            </span>
          ))}
          {filterEmailStatus.map(e => {
            const label = EMAIL_STATUS_OPTIONS.find(o => o.value === e)?.label ?? e;
            return (
              <span key={e} className="ld-active-tag">
                <Mail size={11} /> {label}
                <button onClick={() => toggleValue(setFilterEmailStatus, e)}><X size={10} /></button>
              </span>
            );
          })}
        </div>
      )}

      {/* Bulk action bar */}
      {(selected.length > 0 || allPagesSelected) && (
        <div className="ld-bulk-bar">
          <span className="ld-bulk-count">{displaySelectedCount.toLocaleString()} selected</span>
          <div className="ld-bulk-actions">
            <button className="btn btn-sm btn-secondary" onClick={openSegmentModal}><Tag size={14} /> Add to Segment</button>
            {!allPagesSelected && deletableLeads.length > 0 && (
              <button className="btn btn-sm btn-danger" onClick={() => setConfirmDeleteLeads(true)}>Delete</button>
            )}
          </div>
        </div>
      )}

      {/* Select-all-pages banner */}
      {allCurrentPageSelected && !allPagesSelected && totalLeads > leads.length && (
        <div className="ld-select-all-banner">
          All {leads.length} leads on this page are selected.{' '}
          <button className="ld-select-all-banner-btn" onClick={selectAllPages}>
            Select all {totalLeads.toLocaleString()} leads matching your search
          </button>
        </div>
      )}
      {allPagesSelected && (
        <div className="ld-select-all-banner">
          All {totalLeads.toLocaleString()} leads are selected.{' '}
          <button className="ld-select-all-banner-btn" onClick={clearAllPagesSelection}>
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card ld-card">
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead>
              <tr className="ld-thead-row">
                <th className="ld-th ld-th-check">
                  <input type="checkbox" className="ld-checkbox" checked={allCurrentPageSelected} onChange={toggleAll} />
                </th>
                <th className="ld-th ld-th-name">Name</th>
                <th className="ld-th ld-th-company">Company</th>
                <th className="ld-th ld-th-industry">Industry</th>
                <th className="ld-th ld-th-location">Location</th>
                <th className="ld-th ld-th-source">Source</th>
                <th className="ld-th ld-th-title">Job Title</th>
                <th className="ld-th ld-th-more"></th>
              </tr>
            </thead>
            <tbody>
              {!hasQuery && !showByDefault && (
                <tr>
                  <td colSpan={10}>
                    <div className="ld-prompt-state">
                      <Search size={36} color="var(--gray-300)" />
                      <p className="ld-prompt-title">Search or filter to discover leads</p>
                      <p className="ld-prompt-sub">Use the search bar or filters above to find leads from your Internal Database.</p>
                    </div>
                  </td>
                </tr>
              )}
              {shouldShowLeads && isLoading && leads.length === 0 && (
                <tr>
                  <td colSpan={10} className="ld-td ld-td-state">
                    Loading leads…
                  </td>
                </tr>
              )}
              {shouldShowLeads && isError && (
                <tr>
                  <td colSpan={10} className="ld-td ld-td-state">
                    Failed to load leads. Please check your Internal Database integration settings.
                  </td>
                </tr>
              )}
              {shouldShowLeads && !isLoading && !isError && leads.length === 0 && (
                <tr>
                  <td colSpan={10} className="ld-td ld-td-state">
                    No results found{committedSearch ? ` for "${committedSearch}"` : ''}. Try adjusting your filters.
                  </td>
                </tr>
              )}
              {leads.map((lead) => (
                <tr key={lead.id} className={`ld-row ${selected.includes(lead.id) ? 'ld-row--selected' : ''}`}>
                  <td className="ld-td ld-td-check">
                    <input type="checkbox" className="ld-checkbox" checked={selected.includes(lead.id)} onChange={() => toggleSelect(lead)} disabled={!!lead.is_unsubscribed} />
                  </td>
                  <td className="ld-td ld-td-name">
                    <Link
                      to={`/app/leads/${lead.id}`}
                      className="ld-lead-link"
                      state={{ searchContext: { query: committedSearch, source: filterSource, titles: filterTitles, locations: filterLocations, company: filterCompany, industry: filterIndustry, seniority: filterSeniority, emailStatus: filterEmailStatus } }}
                    >
                      <div className="ld-avatar">
                        {lead.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <span className="ld-lead-name">{lead.name}</span>
                    </Link>
                  </td>
                  <td className="ld-td ld-td-muted">{lead.company}</td>
                  <td className="ld-td">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      {lead.industry
                        ? <span className="badge badge-blue">{lead.industry}</span>
                        : <span className="ld-td-muted">-</span>}
                      {lead.is_unsubscribed && (
                        isAdminPlan ? (
                          <button
                            className="badge badge-red ldp-unsub-btn"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); setResubscribeLeadId(lead.id); }}
                            title="Click to remove from unsubscribe list"
                          >
                            Unsubscribed
                          </button>
                        ) : (
                          <span className="badge badge-red">Unsubscribed</span>
                        )
                      )}
                    </div>
                  </td>
                  <td className="ld-td ld-td-muted">{lead.location}</td>
                  <td className="ld-td"><span className={`badge ${sourceBadge(lead.source)}`}>{sourceLabel(lead.source)}</span></td>
                  <td className="ld-td ld-td-muted">{lead.title || <span className="ld-td-muted">-</span>}</td>
                  <td className="ld-td ld-td-more">
                    <Link to={`/app/leads/${lead.id}`} className="ld-detail-btn" aria-label="View details"
                      state={{ searchContext: { query: committedSearch, source: filterSource, titles: filterTitles, locations: filterLocations, company: filterCompany, industry: filterIndustry, seniority: filterSeniority, emailStatus: filterEmailStatus } }}>
                      <Eye size={16} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalLeads > 0 && (
        <div className="ld-pagination">
          <p className="ld-pagination-count">
            {`Showing ${showingFrom}-${showingTo} of ${totalLeads.toLocaleString()} leads`}
          </p>
          <div className="ld-pagination-right">
            {/* Records per page selector */}
            <div className="ld-per-page">
              <span className="ld-per-page-label">Per page:</span>
              <select
                className="ld-per-page-select"
                value={perPage}
                onChange={e => setPerPage(Number(e.target.value))}
              >
                {[500, 1000, 1500, 2000].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            {lastPage > 1 && (
              <>
                <div className="ld-pagination-controls">
                  <button
                    className="btn btn-secondary btn-sm ld-pag-arrow"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {page > 2 && <button className="ld-pag-btn" onClick={() => setPage(1)}>1</button>}
                  {page > 3 && <span className="ld-pag-ellipsis">...</span>}
                  {page > 1 && <button className="ld-pag-btn" onClick={() => setPage(page - 1)}>{page - 1}</button>}
                  <button className="ld-pag-btn ld-pag-btn--active">{page}</button>
                  {page < lastPage && <button className="ld-pag-btn" onClick={() => setPage(page + 1)}>{page + 1}</button>}
                  {page < lastPage - 2 && <span className="ld-pag-ellipsis">...</span>}
                  {page < lastPage - 1 && <button className="ld-pag-btn ld-pag-mid" onClick={() => setPage(lastPage)}>{lastPage}</button>}
                  <button
                    className="btn btn-secondary btn-sm ld-pag-arrow"
                    onClick={() => setPage(p => Math.min(lastPage, p + 1))}
                    disabled={page === lastPage || lastPage === 0}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* Go to page */}
                <div className="ld-goto-page">
                  <span className="ld-goto-page-label">Go to:</span>
                  <input
                    type="number"
                    className="ld-goto-page-input"
                    value={goToPageInput}
                    onChange={e => { setGoToPageInput(e.target.value); setGoToPageError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleGoToPage(); }}
                    placeholder="Page"
                    min={1}
                    max={lastPage}
                  />
                  <button className="btn btn-secondary btn-sm" onClick={handleGoToPage}>Go</button>
                  {goToPageError && <span className="ld-goto-page-error">{goToPageError}</span>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      </>)}

      {/* My Segments tab content */}
      {activeTab === 'My Segments' && (
        <div className="ldseg-wrap">
          {segmentsLoading ? (
            <div className="ldseg-empty"><Loader2 size={24} className="ldseg-spin" /><span>Loading segments...</span></div>
          ) : segments.length === 0 ? (
            <div className="card ldseg-empty">
              <p className="ldseg-empty-title">No segments yet</p>
              <p className="ldseg-empty-sub">Select leads from <button className="ldseg-tab-link" onClick={() => setActiveTab('All Leads')}>All Leads</button> and click "Add to Segment" to create one.</p>
            </div>
          ) : (
            <>
              {/* Segment bulk action bar */}
              {selectedSegment && (
                <div className="ld-bulk-bar ld-bulk-bar--sm">
                  <span className="ld-bulk-count">1 segment selected</span>
                  <div className="ld-bulk-actions">
                    <Link to={`/app/workflows/new?segment=${selectedSegment}`} className="btn btn-sm btn-primary">
                      <Workflow size={14} /> Build Workflow
                    </Link>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => {
                        const seg = segments.find(s => s.id === selectedSegment);
                        if (seg) setConfirmDeleteSeg({ id: seg.id, name: seg.name });
                        setSelectedSegment(null);
                      }}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              )}

              <div className="card ldseg-table-card">
                <table className="ldseg-table">
                  <thead>
                    <tr className="ldseg-thead-row">
                      <th className="ldseg-th ldseg-th-check"></th>
                      <th className="ldseg-th">Segment Name</th>
                      <th className="ldseg-th">Leads</th>
                      <th className="ldseg-th">Created</th>
                      <th className="ldseg-th ldseg-th-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map(seg => (
                      <tr
                        key={seg.id}
                        className={`ldseg-row ${selectedSegment === seg.id ? 'ldseg-row--selected' : ''}`}
                        onClick={() => navigate(`/app/segments/${seg.id}`)}
                      >
                        <td className="ldseg-td ldseg-td-check" onClick={e => { e.stopPropagation(); setSelectedSegment(prev => prev === seg.id ? null : seg.id); }}>
                          <input
                            type="checkbox"
                            className="ld-checkbox"
                            checked={selectedSegment === seg.id}
                            onChange={() => {}}
                          />
                        </td>
                        <td className="ldseg-td">
                          <div className="ldseg-name-cell">
                            <Tag size={15} color="var(--sky)" />
                            <span className="ldseg-name">{seg.name}</span>
                          </div>
                        </td>
                        <td className="ldseg-td ldseg-td-muted">{seg.lead_count.toLocaleString()}</td>
                        <td className="ldseg-td ldseg-td-muted">{new Date(seg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td className="ldseg-td ldseg-td-actions" onClick={e => e.stopPropagation()}>
                          <Link
                            to={`/app/workflows/new?segment=${seg.id}`}
                            className="ldseg-action-btn"
                            title="Build Workflow"
                          >
                            <Workflow size={15} />
                          </Link>
                          <button
                            className="ldseg-delete-btn"
                            aria-label="Delete segment"
                            onClick={() => setConfirmDeleteSeg({ id: seg.id, name: seg.name })}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Recently Viewed tab content */}
      {activeTab === 'Recently Viewed' && (
        <div className="ldseg-wrap">
          {recentLeads.length === 0 ? (
            <div className="card ldseg-empty">
              <p className="ldseg-empty-title">No recently viewed leads</p>
              <p className="ldseg-empty-sub">Leads you open will appear here so you can quickly return to them.</p>
            </div>
          ) : (
            <>
              <div className="ld-recent-header">
                <button className="ld-clear-all-btn" onClick={clearRecentLeads}>
                  <X size={12} /> Clear history
                </button>
              </div>
              <div className="card ld-card">
                <div className="ld-table-wrap">
                  <table className="ld-table">
                    <thead>
                      <tr className="ld-thead-row">
                        <th className="ld-th ld-th-name">Name</th>
                        <th className="ld-th ld-th-company">Company</th>
                        <th className="ld-th ld-th-industry">Industry</th>
                        <th className="ld-th ld-th-source">Source</th>
                        <th className="ld-th ld-th-ctx">Search Context</th>
                        <th className="ld-th ld-th-viewed">Viewed</th>
                        <th className="ld-th ld-th-more"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentLeads.map(lead => {
                        const ctx = lead.searchContext;
                        const hasCtx = ctx && (ctx.query || (ctx.source?.length ?? 0) > 0 || (ctx.titles?.length ?? 0) > 0);
                        return (
                        <tr
                          key={lead.id}
                          className={`ld-row${hasCtx ? ' ld-row-pointer' : ''}`}
                          onClick={hasCtx ? () => {
                            setActiveTab('All Leads');
                            setSearchQuery(ctx!.query ?? '');
                            setCommittedSearch(ctx!.query ?? '');
                            setFilterSource(ctx!.source ?? []);
                            setFilterTitles(ctx!.titles ?? []);
                            setFilterLocations(ctx!.locations ?? []);
                            setFilterCompany(ctx!.company ?? []);
                            setFilterIndustry((ctx as any).industry ?? []);
                            setFilterSeniority((ctx as any).seniority ?? []);
                            setFilterEmailStatus((ctx as any).emailStatus ?? []);
                          } : undefined}
                        >
                          <td className="ld-td ld-td-name">
                            <Link
                              to={`/app/leads/${lead.id}`}
                              className="ld-lead-link"
                              onClick={e => e.stopPropagation()}
                              state={{ searchContext: ctx, returnTab: 'Recently Viewed' }}
                            >
                              <div className="ld-avatar">
                                {lead.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                              <span className="ld-lead-name">{lead.name}</span>
                            </Link>
                          </td>
                          <td className="ld-td ld-td-muted">{lead.company}</td>
                          <td className="ld-td">
                            {lead.industry
                              ? <span className="badge badge-blue">{lead.industry}</span>
                              : <span className="ld-td-muted">-</span>}
                          </td>
                          <td className="ld-td"><span className={`badge ${sourceBadge(lead.source)}`}>{sourceLabel(lead.source)}</span></td>
                          <td className="ld-td ld-td-sm">
                            {hasCtx ? (
                              <div className="ld-ctx-tags">
                                {ctx!.query && (
                                  <span className="ld-ctx-tag-primary">
                                    "{ctx!.query}"
                                  </span>
                                )}
                                {(ctx!.source ?? []).map(s => (
                                  <span key={s} className="ld-ctx-tag">{s}</span>
                                ))}
                                {(ctx!.titles ?? []).map(t => (
                                  <span key={t} className="ld-ctx-tag">{t}</span>
                                ))}
                                {(ctx!.locations ?? []).map(l => (
                                  <span key={l} className="ld-ctx-tag">{l}</span>
                                ))}
                              </div>
                            ) : <span className="ld-td-muted">—</span>}
                          </td>
                          <td className="ld-td ld-td-muted ld-td-sm">
                            {new Date(lead.viewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            {' · '}
                            {new Date(lead.viewedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </td>
                          <td className="ld-td ld-td-more">
                            <Link to={`/app/leads/${lead.id}`} className="ld-detail-btn" aria-label="View details"
                              onClick={e => e.stopPropagation()} state={{ searchContext: ctx, returnTab: 'Recently Viewed' }}>
                              <Eye size={16} />
                            </Link>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Add to Segment modal */}
      {showSegmentModal && (
        <div className="ldsm-overlay" onClick={() => setShowSegmentModal(false)}>
          <div className="ldsm-modal" onClick={e => e.stopPropagation()}>
            <div className="ldsm-header">
              <h2 className="ldsm-title">Add to Segment</h2>
              <button className="ldsm-close" onClick={() => setShowSegmentModal(false)}><X size={18} /></button>
            </div>

            <p className="ldsm-subtitle">{displaySelectedCount.toLocaleString()} lead{displaySelectedCount !== 1 ? 's' : ''} selected</p>

            {/* Create new segment */}
            <div className="ldsm-section">
              <p className="ldsm-section-label">Create new segment</p>
              <div className="ldsm-input-row">
                <input
                  className="ldsm-input"
                  placeholder="Segment name..."
                  value={segmentName}
                  onChange={e => { setSegmentName(e.target.value); setSegmentError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleCreateSegment()}
                  autoFocus
                />
                <button
                  className="btn btn-primary btn-sm ldsm-create-btn"
                  onClick={handleCreateSegment}
                  disabled={isSaving}
                >
                  {isSaving && createSegmentMutation.isPending ? <Loader2 size={14} className="ldseg-spin" /> : <Plus size={14} />}
                  Create
                </button>
              </div>
              {segmentError && <p className="ldsm-error">{segmentError}</p>}
            </div>

            {/* Add to existing */}
            {segments.length > 0 && (
              <div className="ldsm-section">
                <p className="ldsm-section-label">Or add to existing segment</p>
                <div className="ldsm-existing-list">
                  {segments.map(seg => (
                    <button
                      key={seg.id}
                      className="ldsm-existing-item"
                      onClick={() => handleAddToExisting(seg.id)}
                      disabled={isSaving}
                    >
                      <Tag size={14} color="var(--sky)" />
                      <span className="ldsm-existing-name">{seg.name}</span>
                      <span className="ldsm-existing-count">{seg.lead_count}</span>
                      {addToSegmentMutation.isPending ? <Loader2 size={14} className="ldseg-spin" /> : <Plus size={14} color="var(--sky)" />}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmDeleteLeads && (
        <ConfirmModal
          title="Delete Leads"
          message={`Are you sure you want to delete ${deletableLeads.length} lead${deletableLeads.length !== 1 ? 's' : ''}? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => {
            deleteLeadsMutation.mutate(deletableLeads.map(l => l.id));
            setConfirmDeleteLeads(false);
            setSelected([]); setSelectedLeadMap({});
          }}
          onCancel={() => setConfirmDeleteLeads(false)}
        />
      )}

      {confirmDeleteSeg && (
        <ConfirmModal
          title="Delete Segment"
          message={`Are you sure you want to delete "${confirmDeleteSeg.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => {
            deleteSegmentMutation.mutate(confirmDeleteSeg.id);
            setConfirmDeleteSeg(null);
          }}
          onCancel={() => setConfirmDeleteSeg(null)}
        />
      )}

      {/* Import CSV modal */}
      {showImportModal && (
        <div className="ldsm-overlay" onClick={handleCloseImportModal}>
          <div className="ldsm-modal ldim-modal" onClick={e => e.stopPropagation()}>
            <div className="ldsm-header">
              <h2 className="ldsm-title">Import Leads from CSV</h2>
              <button className="ldsm-close" onClick={handleCloseImportModal}><X size={18} /></button>
            </div>

            <div className="ldsm-section">
              <p className="ldim-desc">
                Upload a CSV file with lead data.{' '}
                <a
                  href="/sample_leads_import.csv"
                  download="sample_leads_import.csv"
                  className="ldim-sample-link"
                >
                  Download sample template
                </a>{' '}
                to see the required format.
              </p>

              {!importResult && (
                <>
                  <div className="ldim-file-wrap">
                    <label className="ldim-file-label">
                      <Upload size={16} />
                      {importFile ? importFile.name : 'Choose CSV file…'}
                      <input
                        type="file"
                        accept=".csv"
                        className="ldim-file-input"
                        onChange={e => setImportFile(e.target.files?.[0] ?? null)}
                      />
                    </label>
                  </div>

                  <div className="ldim-actions">
                    <button className="btn btn-secondary btn-sm" onClick={handleCloseImportModal}>Cancel</button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleImport}
                      disabled={!importFile || importing}
                    >
                      {importing ? <Loader2 size={14} className="ldseg-spin" /> : <Upload size={14} />}
                      {importing ? 'Importing…' : 'Import'}
                    </button>
                  </div>
                </>
              )}

              {importResult && (
                <div className="ldim-result">
                  <div className="ldim-result-stats">
                    <div className="ldim-stat ldim-stat--success">
                      <span className="ldim-stat-num">{importResult.imported}</span>
                      <span className="ldim-stat-label">Imported</span>
                    </div>
                    <div className="ldim-stat ldim-stat--skip">
                      <span className="ldim-stat-num">{importResult.skipped}</span>
                      <span className="ldim-stat-label">Skipped</span>
                    </div>
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="ldim-errors">
                      {importResult.errors.map((e, i) => (
                        <p key={i} className="ldim-error-item">{e}</p>
                      ))}
                    </div>
                  )}
                  <div className="ldim-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => { setImportResult(null); setImportFile(null); }}>
                      Import Another
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handleCloseImportModal}>Done</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {resubscribeLeadId && (() => {
        const lead = leads.find(l => l.id === resubscribeLeadId);
        return (
          <ConfirmModal
            title="Remove from Unsubscribe List"
            message={`Remove ${lead?.name ?? 'this lead'} from the unsubscribe list? They will be eligible to receive emails again.`}
            confirmLabel="Remove"
            danger={false}
            onConfirm={() => {
              removeUnsubscribeMutation.mutate(resubscribeLeadId);
              setResubscribeLeadId(null);
            }}
            onCancel={() => setResubscribeLeadId(null)}
          />
        );
      })()}
    </div>
  );
}
